import { useState, useCallback, useRef } from 'react';
import { GameState, Position, getPieceAt, positionToAlgebraic, Piece } from '@/lib/chess-engine';
import { useTouchGestures, SwipeGesture } from '@/hooks/useTouchGestures';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { modernPieceImages } from '@/lib/pieces';

interface NativeChessBoardProps {
  gameState: GameState;
  onSquareClick: (position: Position) => void;
  highlightedSquares?: Position[];
  selectedSquare?: Position | null;
  flipped?: boolean;
  enableSwipeGestures?: boolean;
  enableHapticFeedback?: boolean;
  fillParent?: boolean;
  compactPadding?: boolean;
}

export function NativeChessBoard({ 
  gameState, 
  onSquareClick, 
  highlightedSquares = [], 
  selectedSquare = null,
  flipped = false,
  enableSwipeGestures = true,
  enableHapticFeedback = true,
  fillParent = false,
  compactPadding = false
}: NativeChessBoardProps) {
  const [draggedPiece, setDraggedPiece] = useState<{ piece: string; from: Position } | null>(null);
  const [liftedPiece, setLiftedPiece] = useState<Position | null>(null);
  const [settlingPiece, setSettlingPiece] = useState<Position | null>(null);
  const [swipeTrails, setSwipeTrails] = useState<Array<{ id: string; x: number; y: number }>>([]);
  const boardRef = useRef<HTMLDivElement>(null);
  
  const { triggerHaptic } = useHapticFeedback();

  const isSquareHighlighted = (row: number, col: number): boolean => {
    return highlightedSquares.some(([r, c]) => r === row && c === col);
  };

  const isSquareSelected = (row: number, col: number): boolean => {
    return selectedSquare && selectedSquare[0] === row && selectedSquare[1] === col;
  };

  const getSquareColor = (row: number, col: number): string => {
    const isLight = (row + col) % 2 === 0;
    const baseColor = isLight ? 'chess-square-light' : 'chess-square-dark';
    
    if (isSquareSelected(row, col)) {
      return 'chess-square-selected';
    }
    if (isSquareHighlighted(row, col)) {
      return 'chess-square-highlight';
    }
    return baseColor;
  };

  const handleSquareClick = useCallback((row: number, col: number) => {
    const position: Position = [row, col];
    const piece = getPieceAt(gameState.board, position);
    
    // Haptic feedback for interactions
    if (enableHapticFeedback) {
      if (piece && piece.color === gameState.sideToMove) {
        triggerHaptic('selection');
      } else if (selectedSquare) {
        // This is a move attempt - we'll trigger success/invalid after validation
        const wasValidMove = true; // This would be determined by the parent component
        triggerHaptic(wasValidMove ? 'success' : 'invalid');
      }
    }
    
    onSquareClick(position);
  }, [gameState.board, gameState.sideToMove, selectedSquare, enableHapticFeedback, triggerHaptic, onSquareClick]);

  const getSquareFromPosition = useCallback((x: number, y: number): Position | null => {
    if (!boardRef.current) return null;
    
    const rect = boardRef.current.getBoundingClientRect();
    const squareWidth = rect.width / 8;
    const squareHeight = rect.height / 8;
    
    const col = Math.floor((x - rect.left) / squareWidth);
    const row = Math.floor((y - rect.top) / squareHeight);
    
    if (row < 0 || row > 7 || col < 0 || col > 7) return null;
    
    return flipped ? [7 - row, 7 - col] : [row, col];
  }, [flipped]);

  // Helper to get sprite position for piece type (no longer needed, kept for reference)
  // const getPieceSpritePosition = (piece: Piece): number => {
  //   const positions: { [key: string]: number } = {
  //     'K': 0, 'Q': 1, 'R': 2, 'B': 3, 'N': 4, 'P': 5
  //   };
  //   return positions[piece.type.toUpperCase()] || 0;
  // };

  const getSquareDirection = useCallback((from: Position, direction: SwipeGesture['direction']): Position | null => {
    const [fromRow, fromCol] = from;
    
    const directionMap: Record<SwipeGesture['direction'], [number, number]> = {
      'N': [-1, 0], 'NE': [-1, 1], 'E': [0, 1], 'SE': [1, 1],
      'S': [1, 0], 'SW': [1, -1], 'W': [0, -1], 'NW': [-1, -1]
    };
    
    const [deltaRow, deltaCol] = directionMap[direction];
    
    // For chess pieces, we want to move multiple squares in the swipe direction
    // Try distances from 1 to 7 squares to find valid moves
    for (let distance = 1; distance <= 7; distance++) {
      const newRow = fromRow + (deltaRow * distance);
      const newCol = fromCol + (deltaCol * distance);
      
      if (newRow >= 0 && newRow <= 7 && newCol >= 0 && newCol <= 7) {
        const targetPosition: Position = [newRow, newCol];
        
        // Return the furthest valid position in this direction
        // The parent component will validate if this is a legal chess move
        if (distance === 1 || getPieceAt(gameState.board, targetPosition) !== null) {
          return targetPosition;
        }
      }
    }
    
    return null;
  }, [gameState.board]);

  const addSwipeTrail = useCallback((x: number, y: number) => {
    if (!boardRef.current) return;
    
    const rect = boardRef.current.getBoundingClientRect();
    const trailId = `trail-${Date.now()}-${Math.random()}`;
    
    setSwipeTrails(prev => [...prev, { 
      id: trailId, 
      x: x - rect.left, 
      y: y - rect.top 
    }]);
    
    // Remove trail after animation
    setTimeout(() => {
      setSwipeTrails(prev => prev.filter(trail => trail.id !== trailId));
    }, 300);
  }, []);

  const handleSwipeGesture = useCallback((gesture: SwipeGesture) => {
    const startSquare = getSquareFromPosition(gesture.startPosition.x, gesture.startPosition.y);
    if (!startSquare) return;
    
    const piece = getPieceAt(gameState.board, startSquare);
    if (!piece || piece.color !== gameState.sideToMove) return;
    
    const targetSquare = getSquareDirection(startSquare, gesture.direction);
    if (!targetSquare) return;
    
    // Add visual trail effect
    addSwipeTrail(gesture.endPosition.x, gesture.endPosition.y);
    
    // Lift animation for the piece
    setLiftedPiece(startSquare);
    setTimeout(() => setLiftedPiece(null), 200);
    
    // First select the piece if not already selected
    if (!selectedSquare || (selectedSquare[0] !== startSquare[0] || selectedSquare[1] !== startSquare[1])) {
      handleSquareClick(startSquare[0], startSquare[1]);
      
      // Then attempt the move after a brief delay
      setTimeout(() => {
        handleSquareClick(targetSquare[0], targetSquare[1]);
        
        // Add settling animation
        setSettlingPiece(targetSquare);
        setTimeout(() => setSettlingPiece(null), 400);
      }, 100);
    } else {
      // Already selected, just make the move
      handleSquareClick(targetSquare[0], targetSquare[1]);
      
      // Add settling animation
      setSettlingPiece(targetSquare);
      setTimeout(() => setSettlingPiece(null), 400);
    }
  }, [getSquareFromPosition, gameState.board, gameState.sideToMove, getSquareDirection, addSwipeTrail, selectedSquare, handleSquareClick]);

  const handleTapGesture = useCallback((position: { x: number; y: number }) => {
    const square = getSquareFromPosition(position.x, position.y);
    if (square) {
      handleSquareClick(square[0], square[1]);
    }
  }, [getSquareFromPosition, handleSquareClick]);

  const handleLongPressGesture = useCallback((position: { x: number; y: number }) => {
    const square = getSquareFromPosition(position.x, position.y);
    if (!square) return;
    
    const piece = getPieceAt(gameState.board, square);
    if (piece && piece.color === gameState.sideToMove) {
      // Show all legal moves for this piece (handled by parent component)
      handleSquareClick(square[0], square[1]);
      
      if (enableHapticFeedback) {
        triggerHaptic('selection');
      }
    }
  }, [getSquareFromPosition, gameState.board, gameState.sideToMove, handleSquareClick, enableHapticFeedback, triggerHaptic]);

  const { gestureHandlers } = useTouchGestures({
    minSwipeDistance: 25,
    minSwipeVelocity: 0.2,
    onSwipe: enableSwipeGestures ? handleSwipeGesture : undefined,
    onTap: handleTapGesture,
    onLongPress: handleLongPressGesture,
    longPressDelay: 350
  });

  const handleDragStart = (e: React.DragEvent, row: number, col: number) => {
    const piece = getPieceAt(gameState.board, [row, col]);
    if (!piece || piece.color !== gameState.sideToMove) {
      e.preventDefault();
      return;
    }

    const pieceChar = piece.type.toLowerCase();
    const fullPieceChar = piece.color === 'white' ? pieceChar.toUpperCase() : pieceChar;
    setDraggedPiece({ piece: fullPieceChar, from: [row, col] });
    
    // Store data for cross-browser compatibility
    e.dataTransfer.setData('text/plain', `${row},${col}`);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, row: number, col: number) => {
    e.preventDefault();
    
    if (draggedPiece) {
      const [fromRow, fromCol] = draggedPiece.from;
      if (fromRow !== row || fromCol !== col) {
        onSquareClick([row, col]);
      }
    }
    
    setDraggedPiece(null);
  };

  const renderSquare = (row: number, col: number) => {
    const displayRow = flipped ? 7 - row : row;
    const displayCol = flipped ? 7 - col : col;
    const piece = getPieceAt(gameState.board, [displayRow, displayCol]);
    
    const isLifted = liftedPiece && liftedPiece[0] === displayRow && liftedPiece[1] === displayCol;
    const isSettling = settlingPiece && settlingPiece[0] === displayRow && settlingPiece[1] === displayCol;
    
    return (
      <div
        key={`${row}-${col}`}
        className={`
          aspect-square flex items-center justify-center cursor-pointer relative
          transition-colors duration-150 chess-square
          ${getSquareColor(displayRow, displayCol)}
          hover:brightness-110
        `}
        onClick={() => handleSquareClick(displayRow, displayCol)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, displayRow, displayCol)}
      >
            {piece && (
              <img
                src={modernPieceImages[piece.color === 'white' ? piece.type.toUpperCase() : piece.type.toLowerCase()]}
                alt={`${piece.color} ${piece.type}`}
                className={`w-[85%] h-[85%] select-none pointer-events-none ${isLifted ? 'chess-piece-lifted' : ''} ${isSettling ? 'chess-piece-settling' : ''}`}
                draggable={false}
              />
            )}
        
        {/* Square coordinates (for debugging) */}
        <div className="absolute bottom-0 left-0 text-xs text-black/30 leading-none px-0.5">
          {positionToAlgebraic([displayRow, displayCol])}
        </div>
      </div>
    );
  };

  return (
    <div className={fillParent ? "w-full h-full" : `mobile-safe-container w-full h-full ${compactPadding ? 'p-1' : 'p-2'}`}>
      <div className={fillParent ? "w-full h-full" : "chess-board-responsive aspect-square max-w-full"}>
        <div className={`w-full h-full chess-board-container rounded-lg shadow-lg ${fillParent ? '' : (compactPadding ? 'p-1' : 'p-2')} max-w-full`}>
          <div 
            ref={boardRef}
            className="w-full h-full grid grid-cols-8 gap-0 chess-board rounded overflow-hidden relative"
            {...(enableSwipeGestures ? gestureHandlers : {})}
          >
            {Array.from({ length: 8 }, (_, row) =>
              Array.from({ length: 8 }, (_, col) => renderSquare(row, col))
            )}
            
            {/* Swipe trail effects */}
            {swipeTrails.map(trail => (
              <div
                key={trail.id}
                className="chess-swipe-trail"
                style={{
                  left: trail.x - 10,
                  top: trail.y - 10
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}